import { useCallback, useEffect, useRef } from 'react'

import { PaymentProviderQrDto } from '@model/MasterTransactionDto'
import {
  isPaymentProviderBankType,
  isPaymentProviderCryptoType,
  isPaymentProviderPaymentAgentType,
} from '@model/PaymentProviderType'

export type DepositProceedValues = {
  amount?: number
  fields: Record<string, any>
  paymentProvider: {
    id?: string
    name?: string
    description?: string
    providerCategory: { id?: number }
  }
  wallet?: { currency?: { id: string } }
  tradingAccount?: { currency?: { id: string } }
}

interface ProceedModalProps {
  values: DepositProceedValues
  qr?: PaymentProviderQrDto | null
  transactionId?: string
  onProceed(): void
  onClose?(): void
  onQrSuccess?(): void
  onQrDeclined?(): void
}

interface LocalInternetBankingModalProps extends ProceedModalProps {
  providerName: string
}

interface UseDepositProceedRedirectParams {
  pendingRedirectTransactionId?: string
  pendingRedirectValues?: DepositProceedValues
  pendingRedirectQr?: PaymentProviderQrDto | null
  transactionId?: string
  onProceedRedirect?(): void
  onCloseModal?(): void
  onQrSuccess?(): void
  onQrDeclined?(): void
  openCardModal(modalProps: ProceedModalProps): unknown
  openCryptoModal(modalProps: ProceedModalProps): unknown
  openPaymentModal(modalProps: ProceedModalProps): unknown
  openLocalInternetBankingModal(modalProps: LocalInternetBankingModalProps): unknown
  shouldOpenCardModal(categoryId: number | undefined, providerId?: string): boolean
  shouldOpenPaymentModal(categoryId: number | undefined): boolean
  isLocalInternetBankingProvider(values: DepositProceedValues): boolean
  getLocalInternetBankingProviderName(values: DepositProceedValues): string
}

export const useDepositProceedRedirect = (params: UseDepositProceedRedirectParams) => {
  const {
    pendingRedirectTransactionId,
    pendingRedirectValues,
    pendingRedirectQr,
    transactionId,
    onProceedRedirect,
    onCloseModal,
    onQrSuccess,
    onQrDeclined,
    openCardModal,
    openCryptoModal,
    openPaymentModal,
    openLocalInternetBankingModal,
    shouldOpenCardModal,
    shouldOpenPaymentModal,
    isLocalInternetBankingProvider,
    getLocalInternetBankingProviderName,
  } = params

  const lastRedirectIdRef = useRef<string | null>(null)

  const runProceed = useCallback(() => {
    onProceedRedirect?.()
  }, [onProceedRedirect])

  const openProceedModal = useCallback(
    (modalValues: DepositProceedValues) => {
      const categoryId = modalValues.paymentProvider?.providerCategory?.id
      const providerId = modalValues.paymentProvider?.id

      const baseModalProps: ProceedModalProps = {
        values: modalValues,
        qr: pendingRedirectQr,
        transactionId,
        onProceed: runProceed,
        onClose: onCloseModal,
        onQrSuccess,
        onQrDeclined,
      }

      if (isPaymentProviderCryptoType(categoryId)) {
        openCryptoModal(baseModalProps)
        return
      }

      if (shouldOpenCardModal(categoryId, providerId)) {
        openCardModal(baseModalProps)
        return
      }

      if (shouldOpenPaymentModal(categoryId)) {
        if (isLocalInternetBankingProvider(modalValues)) {
          openLocalInternetBankingModal({
            ...baseModalProps,
            providerName: getLocalInternetBankingProviderName(modalValues),
          })
          return
        }

        openPaymentModal(baseModalProps)
        return
      }

      if (isPaymentProviderBankType(categoryId) || isPaymentProviderPaymentAgentType(categoryId)) {
        runProceed()
        return
      }

      runProceed()
    },
    [
      getLocalInternetBankingProviderName,
      isLocalInternetBankingProvider,
      openCardModal,
      openCryptoModal,
      onCloseModal,
      onQrSuccess,
      onQrDeclined,
      openLocalInternetBankingModal,
      openPaymentModal,
      pendingRedirectQr,
      transactionId,
      runProceed,
      shouldOpenCardModal,
      shouldOpenPaymentModal,
    ]
  )

  useEffect(() => {
    if (!pendingRedirectTransactionId || !pendingRedirectValues) {
      lastRedirectIdRef.current = null
      return
    }

    if (lastRedirectIdRef.current === pendingRedirectTransactionId) return

    lastRedirectIdRef.current = pendingRedirectTransactionId
    openProceedModal(pendingRedirectValues)
  }, [openProceedModal, pendingRedirectTransactionId, pendingRedirectValues])
}
